import { useProjectSettings } from '@mobile-mapping/providers';
import {
  BtnClasses,
  IconButton,
  PanelLayout,
  Spinner,
} from '@tcweb/connect-ui';
import { t } from '@trimble/i18n';
import {
  AlignmentSettingsPanel,
  FormattingSettings,
} from '@trimble/ui-components/solidjs';
import { createMemo, createSignal, For, JSX, Show } from 'solid-js';
import { DebugSettings } from './DebugSettings';
import { PreferencesSettings } from './PreferencesSettings';

interface Section {
  title: string;
  expanded: boolean;
  dataTestId: string;
  content(): JSX.Element;
  onExpand?(): void;
}

export interface SettingsTabProps {
  onClose(): void;
}

const titleDataCy = 'settings-tab-title';

export const SettingsTab = (props: SettingsTabProps): JSX.Element => {
  const {
    formattingSettings,
    updateFormattingSettings,
    slopeMeasurementSettings,
    updateSlopeMeasurementSettings,
    alignmentSettings,
    updateAlignmentSettings,
    refreshAlignmentData,
  } = useProjectSettings();

  const [enableDebugSection, setEnableDebugSection] = createSignal(false);

  const staticSections = createMemo<Section[]>(() => [
    {
      title: t('mobileMapping.settings.preferences'),
      expanded: false,
      dataTestId: 'preferences-settings-section',
      content: () => <PreferencesSettings />,
    },
    {
      title: t('mobileMapping.settings.unitSettings'),
      expanded: false,
      dataTestId: 'uom-settings-section',
      content: () => (
        <Show
          when={formattingSettings()}
          keyed
          fallback={<Spinner show={true} />}
        >
          {(settings) => (
            <FormattingSettings
              settings={settings}
              onSettingsChanged={(settings) =>
                void updateFormattingSettings(settings)
              }
              slopeMeasurementSettings={slopeMeasurementSettings()}
              onSlopeMeasurementSettingsChanged={updateSlopeMeasurementSettings}
            />
          )}
        </Show>
      ),
    },
    {
      title: t('mobileMapping.settings.alignmentSettings'),
      expanded: false,
      dataTestId: 'alignment-settings-section',
      onExpand: refreshAlignmentData,
      content: () => (
        <AlignmentSettingsPanel
          settings={alignmentSettings()}
          onSettingsChanged={updateAlignmentSettings}
          lengthUnit={formattingSettings().lengthUnit}
        />
      ),
    },
  ]);

  const sections = createMemo<Section[]>(() => [
    // Optionally adds debug section
    ...(enableDebugSection()
      ? [
          {
            title: t('mobileMapping.settings.debugSettings'),
            expanded: true, // expanded by default
            dataTestId: 'debug-settings-section',
            content: () => <DebugSettings />,
          },
        ]
      : []),
    ...staticSections(),
  ]);

  const handlePanelClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    const titleElement = target.closest(`[data-cy="${titleDataCy}"]`);

    if (titleElement && event.shiftKey && event.altKey) {
      // Prevent text selection while interacting
      event.preventDefault();
      event.stopPropagation();
      window.getSelection()?.removeAllRanges();

      setEnableDebugSection(true);
    }
  };

  return (
    <div data-testid="settings-tab" class="contents" onClick={handlePanelClick}>
      <PanelLayout
        panelTitle={t('mobileMapping.settings.title')}
        titleDataCy={titleDataCy}
        rightActions={
          <IconButton
            color={BtnClasses.TERTIARY}
            icon="tc-icon-content-close"
            size="icon-medium"
            data-cy="settings-tab-close"
            clicked={() => {
              props.onClose();
            }}
          />
        }
        content={
          <For each={sections()}>
            {(section) => {
              const [isExpanded, setIsExpanded] = createSignal(
                section.expanded
              );

              const handleExpandToggle = (): void => {
                const newExpanded = !isExpanded();
                setIsExpanded(newExpanded);
                if (newExpanded) {
                  section.onExpand?.();
                }
              };

              return (
                <div
                  class="border-b border-[#d0d0d7]"
                  data-testid={section.dataTestId}
                  aria-expanded={isExpanded()}
                >
                  <header
                    role="button"
                    class="flex p-[1rem] cursor-pointer select-none hover:bg-[#f3f3f7]"
                    onClick={handleExpandToggle}
                  >
                    <h4 class="flex-1 text-ellipsis font-normal">
                      {section.title}
                    </h4>
                    <i
                      class={`icon-font ${isExpanded() ? 'tc-icon-chevron-up' : 'tc-icon-chevron-down'}`}
                    />
                  </header>

                  <Show when={isExpanded()}>
                    <section class="p-[1rem]">{section.content()}</section>
                  </Show>
                </div>
              );
            }}
          </For>
        }
      />
    </div>
  );
};
